import {
  CFormInput,
  CFormTextarea,
  CFormSelect,
  CButton,
  CRow,
  CCol,
  CTooltip,
  CFormSwitch,
} from "@coreui/react";
import { useFormContext, useFieldArray } from "react-hook-form";
import { Field, SectionCard } from "../__sections";
import { LANGUAGES } from "../../../helpers/constants";
import { langFlag, langLabel } from "../../../helpers/general";
import { colors } from "../../../config/theme";
import PlusIcon from "../../../components/icons/PlusIcon";
import DeleteIcon from "../../../components/icons/DeleteIcon";
import { Controller } from "react-hook-form";
import InfiniteScrollSelect from "../../../components/ui/InfiniteScrollSelect";
import { useAuth } from "../../../hooks/useAuth";

interface Step3Props {
  activeLangTab: number;
  setActiveLangTab: (i: number) => void;
  selectedPlaceOption: Place | null;
  setSelectedPlaceOption: (p: Place | null) => void;
}

export default function Translations({
  activeLangTab,
  setActiveLangTab,
  selectedPlaceOption,
  setSelectedPlaceOption,
}: Step3Props) {
  const {
    register,
    control,
    watch,
    formState: { errors },
  } = useFormContext();

  const {
    fields: translationFields,
    append: appendTranslation,
    remove: removeTranslation,
  } = useFieldArray({ control, name: "translations" });

  const watchedTranslations = watch("translations");
  const { isAdmin } = useAuth();

  return (
    <div className="animate-fade-in">
      <SectionCard
        title="Informations de base"
        subtitle="Le Titre identifie l'activité de façon unique dans les URLs."
      >
        <CRow>
          {/* First Column: Title */}
          <CCol md={6}>
            <Field
              label="Titre"
              required
              error={errors.title?.message as string}
              hint="lettres, chiffres, tirets"
            >
              <CFormInput
                {...register("title")}
                placeholder="ex: santorini-sunset-tour"
                invalid={!!errors.title}
              />
            </Field>
          </CCol>

          <CCol md={5}>
            <Field
              label="Ville"
              required
              error={errors.cityId?.message as string}
            >
              <Controller
                name="cityId"
                control={control}
                render={({ field }) => (
                  <InfiniteScrollSelect
                    id="cityId"
                    isClearable={false}
                    getOptionLabel={(option: City) => option?.label}
                    getOptionValue={(option: City) => option.id}
                    isOptionSelected={(option: City, selectedValue: City[]) =>
                      option?.id === selectedValue?.[0]?.id
                    }
                    formatOptions={(cities: City[]) =>
                      [...cities].sort((a, b) =>
                        a.label.localeCompare(b.label, undefined, {
                          sensitivity: "base",
                          usage: "sort",
                        }),
                      )
                    }
                    url={{ path: "cities/all" }}
                    name={field.name}
                    value={field.value ? [field.value] : []}
                    onBlur={field.onBlur}
                    ref={field.ref}
                    onChange={(selected: any) => {
                      const option = Array.isArray(selected)
                        ? selected[0]
                        : selected;
                      field.onChange(option ?? null);
                    }}
                  />
                )}
              />
            </Field>
          </CCol>
          <CCol md={6}>
            <Field
              label="Commission partenaire (%)"
              error={errors.partnerCommission?.message as string}
              hint="Utilisée sur venez-partners et les liens affiliés"
            >
              <CFormInput
                type="number"
                min={0}
                max={100}
                step="0.01"
                {...register("partnerCommission")}
                placeholder="Ex: 10"
                invalid={!!errors.partnerCommission}
                readOnly={!isAdmin}
              />
            </Field>
          </CCol>

          {/* Second Column: Établissement (Admin only) */}
          {isAdmin && (
            <>
              <CCol md={1} className="d-flex align-items-center pt-2">
                <CFormSwitch
                  id="isActive"
                  label="Actif"
                  {...register("isActive")}
                />
              </CCol>

              <CCol md={6}>
                <Field
                  label="Établissement"
                  required
                  error={errors.placeId?.message as string}
                >
                  {selectedPlaceOption?.isVisible === false && (
                    <div className="small mb-1" style={{ color: "#e05252" }}>
                      (cet établissement est invisible)
                    </div>
                  )}
                  <Controller
                    name="placeId"
                    control={control}
                    render={({ field }) => (
                      <InfiniteScrollSelect
                        id="placeId"
                        isClearable={false}
                        error={errors?.placeId?.message as string}
                        getOptionLabel={(option: Place) => option?.name}
                        getOptionValue={(option: Place) => option.id}
                        isOptionSelected={(
                          option: Place,
                          selectedValue: Place[],
                        ) => option?.id === selectedValue?.[0]?.id}
                        url={{ path: "places" }}
                        name={field.name}
                        value={selectedPlaceOption ? [selectedPlaceOption] : []}
                        onBlur={field.onBlur}
                        ref={field.ref}
                        onChange={(selected: any) => {
                          const option = Array.isArray(selected)
                            ? selected[0]
                            : selected;
                          setSelectedPlaceOption(option ?? null);
                          field.onChange(option?.id ?? "");
                        }}
                      />
                    )}
                  />
                </Field>
              </CCol>
            </>
          )}
        </CRow>
        <div
          className="d-flex align-items-start p-3 mb-4"
          style={{
            backgroundColor: colors.yellow,
            borderRadius: "10px",
            fontSize: "0.88rem",
            color: "#2b6b66",
          }}
        >
          💡&nbsp; Pour les champs "Inclus" ou "Non inclus", utilisez
          <span className="ms-1 fw-bold"> Entrée</span>&nbsp;pour séparer chaque
          élément sur une nouvelle ligne.
        </div>

        <div
          className="d-flex gap-2 mb-4 flex-wrap"
          role="tablist"
          aria-label="Langues de traduction"
        >
          {translationFields.map((field: any, index) => {
            const lang = watchedTranslations?.[index]?.lang || field.lang;
            const hasError = !!(errors.translations as any)?.[index];
            const isActive = activeLangTab === index;
            return (
              <button
                type="button"
                key={field.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => setActiveLangTab(index)}
                className="d-flex align-items-center gap-2 border-0"
                style={{
                  padding: "0.55rem 1rem",
                  borderRadius: "10px",
                  backgroundColor: isActive ? colors.yellow : "#f4f4f4",
                  color: isActive ? "#fff" : "#333",
                  fontWeight: isActive ? 700 : 500,
                  position: "relative",
                }}
              >
                <span>{langFlag(lang)}</span>
                <span>{langLabel(lang)}</span>
                {hasError && (
                  <span
                    style={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      backgroundColor: isActive ? "#fff" : "#e05252",
                      display: "inline-block",
                    }}
                  />
                )}
              </button>
            );
          })}
          <CTooltip content="Ajouter une langue">
            <button
              type="button"
              onClick={() => {
                const used = watchedTranslations?.map((t: any) => t.lang) || [];
                const nextLang =
                  LANGUAGES.find((l) => !used.includes(l.value))?.value || "en";
                appendTranslation({
                  lang: nextLang,
                  title: "",
                  description: "",
                  shortDescription: "",
                  includes: "",
                  notIncludes: "",
                  imprtMustKnow: "",
                  imprtMustBring: "",
                  imprtNotAllowed: "",
                  notSuitedFor: "",
                });
                setActiveLangTab(translationFields.length);
              }}
              className="d-flex align-items-center gap-1 border"
              style={{
                padding: "0.55rem 0.9rem",
                borderRadius: "10px",
                backgroundColor: "#fff",
                borderColor: "#ddd",
                color: colors.primary,
                fontWeight: 600,
              }}
            >
              <PlusIcon width="14" fill={colors.primary} /> Langue
            </button>
          </CTooltip>
        </div>

        {translationFields.map((field: any, index) => {
          if (index !== activeLangTab) return null;
          const errs = (errors.translations as any)?.[index];
          return (
            <div key={field.id}>
              <SectionCard title="Titre & langue">
                <CRow>
                  <CCol md={4}>
                    <Field label="Langue" required error={errs?.lang?.message}>
                      <CFormSelect {...register(`translations.${index}.lang`)}>
                        {LANGUAGES.map((l) => (
                          <option key={l.value} value={l.value}>
                            {l.flag} {l.label}
                          </option>
                        ))}
                      </CFormSelect>
                    </Field>
                  </CCol>
                  <CCol md={8}>
                    <Field label="Titre" required error={errs?.title?.message}>
                      <CFormInput
                        {...register(`translations.${index}.title`)}
                        invalid={!!errs?.title}
                      />
                    </Field>
                  </CCol>
                </CRow>
                {index > 0 && (
                  <div className="text-end">
                    <CButton
                      color="danger"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        removeTranslation(index);
                        setActiveLangTab(Math.max(0, index - 1));
                      }}
                    >
                      <DeleteIcon width="14" fill="#dc3545" /> Supprimer cette
                      langue
                    </CButton>
                  </div>
                )}
              </SectionCard>

              <SectionCard title="Descriptions">
                <Field
                  label="Description courte"
                  hint="1-2 phrases, utilisée dans les listes"
                >
                  <CFormTextarea
                    rows={2}
                    {...register(`translations.${index}.shortDescription`)}
                  />
                </Field>
                <Field label="Description complète">
                  <CFormTextarea
                    rows={5}
                    {...register(`translations.${index}.description`)}
                  />
                </Field>
              </SectionCard>

              <SectionCard title="Ce qui est inclus / non inclus">
                <CRow>
                  <CCol md={6}>
                    <Field label="Inclus">
                      <CFormTextarea
                        rows={4}
                        {...register(`translations.${index}.includes`)}
                        placeholder={
                          "Guide professionnel\nDéjeuner inclus\nTransport A/R"
                        }
                      />
                    </Field>
                  </CCol>
                  <CCol md={6}>
                    <Field label="Non inclus">
                      <CFormTextarea
                        rows={4}
                        {...register(`translations.${index}.notIncludes`)}
                        placeholder={"Pourboires\nBoissons supplémentaires"}
                      />
                    </Field>
                  </CCol>
                </CRow>
              </SectionCard>

              <SectionCard title="Informations pratiques">
                <CRow>
                  <CCol md={6}>
                    <Field label="À savoir impérativement">
                      <CFormTextarea
                        rows={3}
                        {...register(`translations.${index}.imprtMustKnow`)}
                      />
                    </Field>
                  </CCol>
                  <CCol md={6}>
                    <Field label="À apporter impérativement">
                      <CFormTextarea
                        rows={3}
                        {...register(`translations.${index}.imprtMustBring`)}
                      />
                    </Field>
                  </CCol>
                </CRow>
                <CRow>
                  <CCol md={6}>
                    <Field label="Interdit / non autorisé">
                      <CFormTextarea
                        rows={3}
                        {...register(`translations.${index}.imprtNotAllowed`)}
                      />
                    </Field>
                  </CCol>
                  <CCol md={6}>
                    <Field label="Non adapté pour">
                      <CFormTextarea
                        rows={3}
                        {...register(`translations.${index}.notSuitedFor`)}
                      />
                    </Field>
                  </CCol>
                </CRow>
              </SectionCard>
            </div>
          );
        })}
      </SectionCard>
    </div>
  );
}
